import { motion } from 'framer-motion';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import VideoStar from './VideoStar';
import React from 'react';
import { getRandomStarScale } from '../utils/starPositions';

const calculateWeeksLoved = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    const diffTime = Math.abs(today - birthDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
};

const StarPoint = React.memo(({ 
    star, 
    position, 
    index, 
    onMeasurement,
    initialExpandedState = false,
    isTextClickable = true
}) => {
    const navigate = useNavigate();
    const weeksLoved = calculateWeeksLoved(star.dob);
    const [isExpanded, setIsExpanded] = useState(initialExpandedState);
    // fullHeight: measured full text height (in pixels)
    const [fullHeight, setFullHeight] = useState(0);
    // collapsedHeight: the height when clamped (in pixels)
    const [collapsedHeight, setCollapsedHeight] = useState(0);
    // Determines whether the text is truly truncated
    const [isTextTruncated, setIsTextTruncated] = useState(false);
    // Controls whether to apply the line-clamp (ellipsis) on the text
    const [clampText, setClampText] = useState(true);
    // Store actual expanded height after animation completes
    const [expandedHeight, setExpandedHeight] = useState(null);
    // Added for hover effect
    const [isHovered, setIsHovered] = useState(false);

    // Refs for measurement
    const visibleTextRef = useRef(null);
    const hiddenTextRef = useRef(null);
    const expandedContainerRef = useRef(null);

    // Measure the full text height from a hidden element
    useEffect(() => {
        // Use a timeout to ensure fonts are loaded and DOM is fully rendered
        const measureTimer = setTimeout(() => {
            if (hiddenTextRef.current && visibleTextRef.current) {
                // Get the actual height of the content
                const measuredFullHeight = hiddenTextRef.current.scrollHeight;
                
                // Calculate number of characters per line (approx)
                const avgCharWidth = 8; // Approximate character width in pixels for text-sm
                const containerWidth = Math.min(300, window.innerWidth * 0.7); // Responsive width
                const charsPerLine = Math.floor(containerWidth / avgCharWidth);
                
                // Calculate estimated number of lines needed
                const messageLength = star.message?.length || 0;
                const estimatedLines = Math.ceil(messageLength / charsPerLine);
                
                // Apply a generous multiplier to account for line breaks, word wrapping, etc.
                const heightMultiplier = 1.5;
                
                // Calculate a more realistic height with buffer
                const calculatedHeight = Math.max(
                    measuredFullHeight * heightMultiplier, // Apply multiplier to measured height
                    estimatedLines * 24 // Estimate based on line count (24px per line with spacing)
                );
                
                // Set a minimum height of 3 lines (60px) and add extra buffer
                // Use smaller values on smaller screens
                const minHeight = Math.min(60, window.innerWidth * 0.15);
                const buffer = Math.min(33, window.innerWidth * 0.08);
                const finalHeight = Math.max(calculatedHeight, minHeight) + buffer;
                
                // Use this as our full height
                setFullHeight(finalHeight);

                // Compute the collapsed height
                const computedStyle = window.getComputedStyle(visibleTextRef.current);
                const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
                const measuredCollapsed = lineHeight * 2; // two lines
                setCollapsedHeight(measuredCollapsed);

                // Mark text as truncated if text length exceeds what would fit in two lines
                const twoLineCharCount = charsPerLine * 2;
                const truncated = messageLength > twoLineCharCount;
                setIsTextTruncated(truncated);
                
                // Call the measurement callback with the final measurements
                if (onMeasurement) {
                    onMeasurement({
                        fullHeight: finalHeight,
                        collapsedHeight: measuredCollapsed,
                        isTruncated: truncated,
                        messageLength,
                        estimatedLines
                    });
                }
                
                //console.log('Text measurements:', {
                //    measuredHeight: measuredFullHeight,
                //    calculatedHeight: calculatedHeight,
                //    finalHeight: finalHeight,
                //    collapsedHeight: measuredCollapsed,
                //    isTruncated: truncated,
                //    messageLength: messageLength,
                //    estimatedLines: estimatedLines
                //});
            }
        }, 100); // Small delay to ensure rendering is complete
        
        return () => clearTimeout(measureTimer);
    }, [star.message, onMeasurement]);

    // Track actual expanded height after expansion
    useEffect(() => {
        if (isExpanded && expandedContainerRef.current) {
            // After expanding, measure the actual height needed
            const actualExpandedHeight = expandedContainerRef.current.scrollHeight;
            if (actualExpandedHeight > fullHeight) {
                setExpandedHeight(actualExpandedHeight + 20); // Add extra buffer
                //console.log('Adjusted height:', actualExpandedHeight + 20);
            }
        } else {
            setExpandedHeight(null);
        }
    }, [isExpanded, fullHeight]);

    // For the star and text scaling
    const { randomScale, textScale } = useMemo(() => {
        const randomScale = position?.scale || getRandomStarScale();
        const textScale = Math.min(Math.max(randomScale, 0.5), 1);
        return { randomScale, textScale };
    }, [position?.scale]);

    // Determine if star is close to the left edge of the screen
    const isNearLeftEdge = useMemo(() => {
        if (!position) return false;
        
        // More conservative threshold based on screen width
        // For larger screens, use a fixed pixel value rather than a percentage
        const threshold = window.innerWidth < 768 
            ? Math.min(window.innerWidth * 0.15, 80) // For small screens
            : Math.min(window.innerWidth * 0.1, 120); // For larger screens
            
        const nearLeftEdge = position.x < threshold;
        
        // Log when we find a star near the left edge
        if (nearLeftEdge) {
            //console.log(`Star "${star.name}" is near left edge:`, {
            //    x: position.x,
            //    screenWidth: window.innerWidth,
            //    threshold: threshold,
            //    leftEdgeAdjustment: true
            //});
        }
        
        return nearLeftEdge;
    }, [position, star.name]);

    // Determine if star is close to the right edge of the screen
    const isNearRightEdge = useMemo(() => {
        if (!position) return false;
        
        // More conservative threshold based on screen width
        // For larger screens, use a fixed pixel value rather than a percentage
        const threshold = window.innerWidth < 768 
            ? Math.min(window.innerWidth * 0.15, 80) // For small screens
            : Math.min(window.innerWidth * 0.1, 120); // For larger screens
            
        const nearRightEdge = position.x > (window.innerWidth - threshold);
        
        // Log when we find a star near the right edge
        if (nearRightEdge) {
            //console.log(`Star "${star.name}" is near right edge:`, {
            //    x: position.x,
            //    screenWidth: window.innerWidth,
            //    threshold: threshold,
            //    rightEdgeAdjustment: true
            //});
        }
        
        return nearRightEdge;
    }, [position, star.name]);

    // Calculate offset for stars near the edges to keep text in viewport
    const edgeOffset = useMemo(() => {
        if ((!isNearLeftEdge && !isNearRightEdge) || !position) return 0;
        
        // Adaptive text container width based on screen size
        const containerWidth = Math.min(350, window.innerWidth * 0.8); // Cap at 350px, scale down for small screens
        const textHalfWidth = (containerWidth / 2) * textScale;
        
        // Handle left edge
        if (isNearLeftEdge) {
            const distanceFromLeftEdge = position.x;
            // Smaller buffer for smaller screens, more conservative for larger screens
            const buffer = window.innerWidth < 768
                ? Math.min(20, window.innerWidth * 0.05)
                : Math.min(30, window.innerWidth * 0.03);
                
            const minRequiredDistance = textHalfWidth + buffer;
            
            // If star is too close to left edge, calculate offset to push text right
            if (distanceFromLeftEdge < minRequiredDistance) {
                // Apply a more gradual offset based on how close to the edge
                const rawOffset = minRequiredDistance - distanceFromLeftEdge;
                // Apply a dampening factor to make the offset less aggressive
                const dampening = window.innerWidth < 768 ? 0.9 : 0.8;
                const offset = rawOffset * dampening;
                
                //console.log(`Applying left edge offset for "${star.name}":`, {
                //    rawOffset,
                //    appliedOffset: offset,
                //    dampening,
                //    distanceFromLeftEdge,
                //    minRequiredDistance,
                //    screenWidth: window.innerWidth
                //});
                return offset;
            }
        }
        
        // Handle right edge
        if (isNearRightEdge) {
            const distanceFromRightEdge = window.innerWidth - position.x;
            // Smaller buffer for smaller screens, more conservative for larger screens
            const buffer = window.innerWidth < 768
                ? Math.min(20, window.innerWidth * 0.05)
                : Math.min(30, window.innerWidth * 0.03);
                
            const minRequiredDistance = textHalfWidth + buffer;
            
            // If star is too close to right edge, calculate offset to push text left
            if (distanceFromRightEdge < minRequiredDistance) {
                // Apply a more gradual offset based on how close to the edge
                const rawOffset = minRequiredDistance - distanceFromRightEdge;
                // Apply a dampening factor to make the offset less aggressive
                const dampening = window.innerWidth < 768 ? 0.9 : 0.8;
                const offset = -(rawOffset * dampening);
                
                //console.log(`Applying right edge offset for "${star.name}":`, {
                //    rawOffset,
                //    appliedOffset: offset,
                //    dampening,
                //    distanceFromRightEdge,
                //    minRequiredDistance,
                //    screenWidth: window.innerWidth
                //});
                return offset;
            }
        }
        
        return 0;
    }, [isNearLeftEdge, isNearRightEdge, position, star.name, textScale]);

    // Update measurements on window resize
    useEffect(() => {
        const handleResize = () => {
            // Re-measure text if we have refs
            if (hiddenTextRef.current && visibleTextRef.current) {
                // Get the actual height of the content
                const measuredFullHeight = hiddenTextRef.current.scrollHeight;
                
                // Calculate number of characters per line (approx)
                const avgCharWidth = 8; // Approximate character width in pixels for text-sm
                const containerWidth = Math.min(300, window.innerWidth * 0.7); // Responsive width
                const charsPerLine = Math.floor(containerWidth / avgCharWidth);
                
                // Calculate estimated number of lines needed
                const messageLength = star.message?.length || 0;
                const estimatedLines = Math.ceil(messageLength / charsPerLine);
                
                // Apply a generous multiplier to account for line breaks, word wrapping, etc.
                const heightMultiplier = 1.5;
                
                // Calculate a more realistic height with buffer
                const calculatedHeight = Math.max(
                    measuredFullHeight * heightMultiplier, // Apply multiplier to measured height
                    estimatedLines * 24 // Estimate based on line count (24px per line with spacing)
                );
                
                // Set a minimum height of 3 lines (60px) and add extra buffer
                // Use smaller values on smaller screens
                const minHeight = Math.min(60, window.innerWidth * 0.15);
                const buffer = Math.min(30, window.innerWidth * 0.08);
                const finalHeight = Math.max(calculatedHeight, minHeight) + buffer;
                
                // Use this as our full height
                setFullHeight(finalHeight);

                // Compute the collapsed height
                const computedStyle = window.getComputedStyle(visibleTextRef.current);
                const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
                const measuredCollapsed = lineHeight * 2; // two lines
                setCollapsedHeight(measuredCollapsed);
            }
        };
        
        // Add resize listener
        window.addEventListener('resize', handleResize);
        
        // Clean up
        return () => window.removeEventListener('resize', handleResize);
    }, [star.message]);

    const handleTextClick = () => {
        if (isTextClickable) {
            setIsExpanded(!isExpanded);
        }
    };

    const handleHoverStart = () => {
        if (isTextClickable && !isExpanded) {
            setIsHovered(true);
        }
    };

    const handleHoverEnd = () => {
        if (isTextClickable && !isExpanded) {
            setIsHovered(false);
        }
    };

    return (
        <motion.div
            className="absolute"
            style={{
                transform: `translate(${position?.x ?? -10000}px, ${position?.y ?? -10000}px)`,
                display: position ? 'block' : 'none'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: position ? 1 : 0 }}
            transition={{ duration: 2, delay: index * 0.5 }}
        >
            <div className="relative">
                {/* Star */}
                <motion.div
                    className="w-[80px] h-[80px] flex items-center justify-center"
                    style={{
                        transform: `scale(${randomScale})`,
                        position: 'relative',
                        left: '50%',
                        marginLeft: '-40px',
                        transformOrigin: 'center center'
                    }}
                >
                    <VideoStar starType={star.starType} />
                </motion.div>

                {/* Text Container */}
                <div
                    className="absolute"
                    style={{
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: `${Math.max(200, 320 * Math.min(randomScale, 1))}px`, // Match scaled width from getStarRectangle
                        zIndex: 4
                    }}
                >
                    <motion.div
                        className="relative"
                        style={{
                            width: '100%', // Use full width of parent
                            transform: `scale(${textScale})`,
                            transformOrigin: 'bottom center'
                        }}
                    >
                        {/* Vertical Line */}
                        <div
                            className="absolute w-[1px] bg-white opacity-70"
                            style={{
                                left: '50%',
                                transform: 'translateX(-50%)',
                                top: '4px',
                                height: 'calc(100% + 40px)',
                                zIndex: 5
                            }}
                        />

                        {/* Text Content */}
                        <div
                            style={{ 
                                marginLeft: `calc(50% + 10px)`
                            }}
                        >
                            <motion.div
                                className={isTextClickable ? "cursor-pointer" : "cursor-default"}
                                onHoverStart={handleHoverStart}
                                onHoverEnd={handleHoverEnd}
                            >
                                <p className={`text-xl select-none text-still-white font-semibold ${isTextClickable ? "cursor-pointer" : "cursor-default"}`} onClick={handleTextClick}>{star.name.toUpperCase()}</p>
                                <p className={`text-xs select-none text-still-white ${isTextClickable ? "cursor-pointer" : "cursor-default"}`} onClick={handleTextClick}>
                                    {weeksLoved.toLocaleString()} {weeksLoved === 1 ? 'WEEK' : 'WEEKS'} STILL LOVED
                                </p>

                                {/* Plus button - only show when clickable and not expanded */}
                                {isTextClickable && !isExpanded && (
                                    <motion.button
                                        className="text-still-white text-xl font-bold opacity-70 hover:opacity-100 transition-opacity cursor-pointer mt-1"
                                        onClick={() => setIsExpanded(true)}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 0.7 }}
                                        whileHover={{ opacity: 1 }}
                                    >
                                        +
                                    </motion.button>
                                )}

                                {/* Animated text container */}
                                <motion.div
                                    ref={expandedContainerRef}
                                    initial={{ height: initialExpandedState ? (expandedHeight || fullHeight) : 0, opacity: initialExpandedState ? 1: 0 }}
                                    animate={{ 
                                        height: isExpanded ? (expandedHeight || fullHeight) : (isTextClickable && isHovered ? 20 : 0),
                                        opacity: isExpanded ? 1 : (isTextClickable && isHovered ? 0.7 : 0)
                                    }}
                                    transition={{ 
                                        duration: isExpanded ? 0.3 : 0.2,
                                        ease: "easeOut"
                                    }}
                                    style={{
                                        overflow: 'hidden',
                                        transformOrigin: 'bottom',
                                        marginBottom: isExpanded ? '12px' : '0px'
                                    }}
                                    className={isTextClickable ? "cursor-pointer" : "cursor-default"}
                                    onClick={handleTextClick}
                                >
                                    {/* Visible text */}
                                    <p
                                        ref={visibleTextRef}
                                        className="text-sm text-white select-none"
                                        style={{ 
                                            paddingBottom: isExpanded ? '8px' : '0px',
                                            lineHeight: '1.5',
                                            whiteSpace: 'pre-wrap',
                                            opacity: isExpanded ? 1 : (isTextClickable && isHovered ? 0.7 : 0)
                                        }}
                                    >
                                        {star.message}
                                    </p>
                                </motion.div>

                                {/* Hidden element for measuring full text height */}
                                <div
                                    ref={hiddenTextRef}
                                    className="invisible absolute pointer-events-none select-none"
                                    style={{ 
                                        width: Math.min(300, window.innerWidth * 0.7) + 'px', // Responsive width
                                        whiteSpace: 'pre-wrap',
                                        position: 'absolute',
                                        zIndex: -1,
                                        lineHeight: '1.5'
                                    }}
                                >
                                    <p className="text-sm">
                                        {star.message}
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
}, (prevProps, nextProps) => {
    // Only re-render if these props change
    return prevProps.star.usid === nextProps.star.usid &&
           prevProps.position?.x === nextProps.position?.x &&
           prevProps.position?.y === nextProps.position?.y &&
           prevProps.initialExpandedState === nextProps.initialExpandedState &&
           prevProps.isTextClickable === nextProps.isTextClickable;
});

export default StarPoint;